import { useEffect } from 'react'
import { useFlag } from '@calendly/feature-check-react'

import { consentModule } from 'services/consent/consent_module'
import {
  handleConsentStateRequest,
  publishConsentState,
} from 'services/consent/consent_state_publish'

/**
 * Exposes the iframe's resolved `ConsentState` to the embedding host.
 *
 * Wiring:
 *   - Listens for `calendly.consent.state.get.v1` messages from the parent.
 *     The transport verifies the message origin against the URL's `embed_domain`
 *     before responding (same trust model as `ConsentUpdateMessageListener`).
 *   - Subscribes to `consentModule.onChange` and forwards every resolved-state
 *     change to the verified parent. Because `consentModule.onChange` fires only
 *     on real resolved-state changes, the parent receives updates that match
 *     what consumers inside the iframe see.
 *
 * Gating: behind the same `sa_consent_aware_embed` flag as the host-side
 * listener so observability rolls out alongside the input it observes.
 */
export const ConsentStatePublisher = () => {
  const consentModuleEnabled = useFlag('sa_consent_aware_embed', false)

  useEffect(() => {
    if (!consentModuleEnabled) return undefined

    const handler = (event) => {
      handleConsentStateRequest(event, consentModule.getState)
    }
    window.addEventListener('message', handler)
    const unsubscribe = consentModule.onChange(publishConsentState)
    return () => {
      window.removeEventListener('message', handler)
      unsubscribe()
    }
  }, [consentModuleEnabled])

  return null
}
